import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import SearchIcon from '@atlaskit/icon/core/search';
import { media } from '@atlaskit/primitives/responsive';

import { wacToken } from '@/libraries/design/foundations';

import type { CareersSearchFieldProps } from '../types';

/** Underline-only search input plus an accessibly-named submit button (parity fix — the reference's button has no accessible name). */
export const CareersSearchField = ({
	value,
	placeholder,
	onChange,
	onSubmit,
}: CareersSearchFieldProps) => {
	const intl = useIntl();
	const submitLabel = intl.formatMessage({
		id: 'careersGallery.searchField.submitLabel',
		defaultMessage: 'Search',
	});

	return (
		<form
			css={formStyle}
			onSubmit={(event) => {
				event.preventDefault();
				onSubmit();
			}}
		>
			<input
				type="text"
				value={value}
				placeholder={placeholder}
				aria-label={placeholder}
				onChange={(event) => onChange(event.target.value)}
				css={inputStyle}
			/>
			<button
				type="submit"
				aria-label={submitLabel}
				css={submitButtonStyle}
				data-event="clicked"
				data-event-component="button"
				data-event-container="careersGallerySearch"
			>
				<SearchIcon label="" size="small" />
			</button>
		</form>
	);
};

const formStyle = css({
	display: 'flex',
	alignItems: 'center',
	gap: '8px',
	width: '100%',
	[media.above.sm]: {
		width: '584px',
	},
});

const inputStyle = css({
	flex: '1 1 auto',
	width: '100%',
	height: '26px',
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '20px',
	lineHeight: '26px',
	color: wacToken('color.extended.blue1000'),
	backgroundColor: 'transparent',
	border: 'none',
	borderBlockEnd: `1px solid ${wacToken('color.primary.blue')}`,
	borderRadius: 0,
	marginBlockEnd: '20px',
	padding: 0,
	outline: 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&:focus-visible': {
		borderBlockEndWidth: '2px',
		borderBlockEndColor: wacToken('color.extended.blue1000'),
	},
});

const submitButtonStyle = css({
	width: '14px',
	height: '14px',
	padding: 0,
	border: 'none',
	background: 'transparent',
	cursor: 'pointer',
	color: wacToken('color.primary.blue'),
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	flexShrink: 0,
	marginBlockEnd: '20px',
});
